// Atribución de marketing en el alta: UTMs + cookies de Meta (_fbc/_fbp) +
// código de referido → profiles. Extraído del patrón de Auth/AuthCompat para
// reutilizarlo en altas que NO pasan por /auth (EnterpriseCheckout, el alta
// directa con pago — ADR-613; esa pantalla perdía la atribución).
//
// Dos mitades:
//   1. captureAttributionParams() — al MONTAR la pantalla de alta: baja los
//      UTMs/ref de la URL, el referrer de la llegada y las cookies de Meta a
//      localStorage (así sobreviven la navegación y el submit).
//   2. saveSignupAttribution(userId) — tras el signUp exitoso: persiste todo
//      en la fila de profiles (la crea el trigger de la DB al registrarse).
//      Best-effort TOTAL: nunca lanza — jamás debe romper un alta o un pago.
//
// Las DOS pantallas de alta usan estas mismas funciones y no tienen copia propia
// (ADR-2026-07-31-atribucion-una-sola-implementacion): `/auth` por `AuthForm` + `Auth`,
// y el alta por link por `EnterpriseCheckout`. `signupAttribution.test.ts` cuenta que
// siga siendo así — que una pantalla se olvide de una de las tres llamadas no rompe
// nada visible, sólo deja de guardar de dónde vino el cliente.

import { supabase } from './supabase';
import { ensureMetaParams } from './metaParams';

/**
 * Los UTM que se guardan. La lista es el contrato: `captureAttributionParams` los baja
 * de la URL, `metadataDeAtribucion` los manda en el `signUp` y el trigger
 * `handle_new_user` los escribe en `profiles` con ESTOS nombres de columna. Sumar uno
 * acá pide su columna (ver `20260922120000_atribucion_utm_id_y_embudo.sql`).
 *
 * `utm_id` entró el 2026-09-22: llegaba en 387 visitas cada 90 días y no lo guardaba
 * nadie. Es el id numérico de campaña de Meta, el único que sobrevive a que alguien
 * renombre la campaña — y en esta base conviven campañas con nombre (`frio_ventas`) y
 * campañas con id, así que sin él no se pueden juntar.
 */
export const UTM_KEYS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'utm_content',
  'utm_id',
] as const;

export function captureAttributionParams(): void {
  try {
    // Primero el fallback de Meta: si el píxel no corrió, esto materializa
    // _fbc desde el fbclid de la URL y genera _fbp — así las lecturas de
    // cookies de abajo siempre encuentran algo que guardar.
    ensureMetaParams();
    const params = new URLSearchParams(window.location.search);
    for (const key of UTM_KEYS) {
      const val = params.get(key);
      if (val) {
        try {
          localStorage.setItem(`atlas:${key}`, val);
        } catch {}
      }
    }
    const ref = params.get('ref');
    if (ref) {
      try {
        localStorage.setItem('atlas:ref', ref);
      } catch {}
    }
    const getCookie = (name: string): string | null => {
      const match = document.cookie.match(
        new RegExp('(?:^|; )' + name + '=([^;]*)'),
      );
      return match ? decodeURIComponent(match[1]) : null;
    };
    const fbc = getCookie('_fbc');
    const fbp = getCookie('_fbp');
    if (fbc) {
      try {
        localStorage.setItem('atlas:fbc', fbc);
      } catch {}
    }
    if (fbp) {
      try {
        localStorage.setItem('atlas:fbp', fbp);
      } catch {}
    }
    capturarReferrer();
  } catch {}
}

/** Dónde queda el referrer de la llegada. Una sola clave, como los UTM. */
const CLAVE_REFERRER = 'atlas:referrer';

/**
 * Hosts que son una ESCALA del alta, no su origen. Volver de ellos no significa que el
 * visitante viniera de ahí: el alta por Google se va del documento y vuelve con
 * `document.referrer = accounts.google.com`, que es exactamente el dato que no queremos
 * guardar. La regla de "el primero manda" de `capturarReferrer` ya cubre al que llegó
 * con un referrer bueno antes de apretar el botón; esta lista cubre al que llegó SIN
 * ninguno (pasa seguido: el navegador in-app de Instagram no manda referrer) y volvió
 * de Google con uno.
 */
const HOSTS_DE_IDA_Y_VUELTA = ['accounts.google.com'];

/**
 * ¿Este `document.referrer` dice de dónde vino el visitante?
 *
 * Pura y exportada para poder testearla: los tests corren en `environment: "node"`
 * (`vitest.config.ts`), donde no hay `document` ni `location`.
 */
export function referrerExterno(
  referrer: string | null | undefined,
  hostPropio: string,
): boolean {
  const crudo = (referrer ?? '').trim();
  if (!crudo) return false;
  let host: string;
  try {
    host = new URL(crudo).host;
  } catch {
    // Un referrer que no es una URL no es un origen: no hay nada que reportar.
    return false;
  }
  // Navegar DENTRO de la app no es una llegada. Sin esto, cualquier paso del alta que
  // recargue se pisaría a sí mismo con `app.estudio-atlas.com`.
  if (!host || host === hostPropio) return false;
  return !HOSTS_DE_IDA_Y_VUELTA.includes(host);
}

/**
 * Guarda de dónde vino el visitante, UNA vez. El primero manda: lo que interesa es la
 * llegada, no la última página que lo mandó acá.
 */
function capturarReferrer(): void {
  try {
    if (localStorage.getItem(CLAVE_REFERRER)) return;
    const crudo = document.referrer;
    if (!referrerExterno(crudo, window.location.host)) return;
    localStorage.setItem(CLAVE_REFERRER, crudo);
  } catch {}
}

/**
 * De dónde vino el visitante, para el embudo del alta por link. El guardado si lo hay
 * (sobrevive la vuelta del OAuth y cualquier recarga) y si no el vivo.
 *
 * Es el ÚNICO dato de origen que queda para el 48% de las llegadas a `/alta-empresa/`
 * que entran sin ningún UTM (medido el 2026-09-22 sobre 90 días).
 */
export function referrerDeLlegada(): string | null {
  try {
    const guardado = localStorage.getItem(CLAVE_REFERRER);
    if (guardado) return guardado;
    return referrerExterno(document.referrer, window.location.host)
      ? document.referrer
      : null;
  } catch {
    return null;
  }
}

/**
 * Código de referido vigente: el de la URL si está, y si no el que quedó guardado
 * (y de paso lo persiste). Se necesita ANTES de crear el usuario —viaja en la
 * metadata del `signUp` y el trigger `handle_new_user` lo baja a
 * `profiles.ref_code`— y otra vez después, en `saveSignupAttribution`. Por eso es
 * su propia función exportada y no queda inline.
 */
export function refCodeActual(): string | null {
  try {
    const ref = new URLSearchParams(window.location.search).get('ref');
    if (ref) {
      try {
        localStorage.setItem('atlas:ref', ref);
      } catch {}
      return ref;
    }
    return localStorage.getItem('atlas:ref');
  } catch {
    return null;
  }
}

/**
 * Lo que va en `options.data` del `signUp`: el trigger `handle_new_user` lo baja a
 * `profiles` (spec 2026-09-11-invitaciones-equipo §5.3). Reemplaza el UPDATE post-registro,
 * que cuando Supabase exige confirmar el mail corría SIN sesión y sólo entraba porque
 * `profiles` no tenía RLS. Sólo claves con valor: el trigger hace `->>` y un null no pisa nada.
 */
export function metadataDeAtribucion(): Record<string, string> {
  const meta: Record<string, string> = {};
  try {
    for (const key of UTM_KEYS) {
      const v = localStorage.getItem(`atlas:${key}`);
      if (v) meta[key] = v;
    }
    const fbc = localStorage.getItem('atlas:fbc');
    const fbp = localStorage.getItem('atlas:fbp');
    if (fbc) meta.fbc = fbc;
    if (fbp) meta.fbp = fbp;
    const ref = refCodeActual();
    if (ref) meta.ref_code = ref;
  } catch {}
  return meta;
}

// Sólo para altas CON sesión (el login OAuth de `Auth.tsx`): el update de la fila propia
// sigue permitido con RLS. El alta por mail manda `metadataDeAtribucion()` en el signUp.
export async function saveSignupAttribution(userId: string): Promise<void> {
  try {
    const utmData: Record<string, string | null> = {};
    for (const key of UTM_KEYS) {
      utmData[key] = localStorage.getItem(`atlas:${key}`) || null;
    }
    utmData.fbc = localStorage.getItem('atlas:fbc') || null;
    utmData.fbp = localStorage.getItem('atlas:fbp') || null;
    const refCode = refCodeActual();
    if (refCode) utmData.ref_code = refCode;
    const hasAny = Object.values(utmData).some((v) => v !== null);
    if (hasAny) {
      await supabase.from('profiles').update(utmData).eq('id', userId);
    }
  } catch {}
}
